import React, { useState, useMemo } from 'react';
import { Card, Select, Space, Alert, Radio, Row, Col } from 'antd';
import { LineChartOutlined } from '@ant-design/icons';
import LineChart from '@/components/charts/LineChart';
import DualAxisChart from '@/components/charts/DualAxisChart';
import { useHealthStore } from '@/stores/useHealthStore';
import type { TimeRange } from '@/types';

const Charts: React.FC = () => {
  const { bpRecords, bsRecords } = useHealthStore();
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [chartType, setChartType] = useState<'single' | 'dual'>('single');
  const [indicator, setIndicator] = useState<'bp' | 'bs'>('bp');

  // Get date range
  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    
    switch (timeRange) {
      case '7d':
        start.setDate(end.getDate() - 7);
        break;
      case '30d':
        start.setDate(end.getDate() - 30);
        break;
      case '90d':
        start.setDate(end.getDate() - 90);
        break;
      case '1y':
        start.setFullYear(end.getFullYear() - 1);
        break;
    }
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  const dateRange = getDateRange();

  // Filter and prepare data
  const chartData = useMemo(() => {
    const filteredBP = bpRecords.filter(r => r.date >= dateRange.start && r.date <= dateRange.end);
    const filteredBS = bsRecords.filter(r => r.date >= dateRange.start && r.date <= dateRange.end);

    if (chartType === 'single') {
      if (indicator === 'bp') {
        // Blood Pressure - show both systolic and diastolic
        return {
          type: 'bp' as const,
          dates: filteredBP.map(r => r.date),
          systolic: filteredBP.map(r => r.systolic),
          diastolic: filteredBP.map(r => r.diastolic)
        };
      } else {
        // Blood Sugar
        return {
          type: 'bs' as const,
          dates: filteredBS.map(r => r.date),
          values: filteredBS.map(r => r.value)
        };
      }
    } else {
      // Dual axis - BP and BS together
      const allDates = [...new Set([...filteredBP.map(r => r.date), ...filteredBS.map(r => r.date)])].sort();
      
      const bpMap = new Map(filteredBP.map(r => [r.date, r]));
      const bsMap = new Map(filteredBS.map(r => [r.date, r]));

      return {
        dates: allDates,
        bpSystolic: allDates.map(d => bpMap.get(d)?.systolic || null),
        bpDiastolic: allDates.map(d => bpMap.get(d)?.diastolic || null),
        bsValues: allDates.map(d => bsMap.get(d)?.value || null)
      };
    }
  }, [bpRecords, bsRecords, dateRange, chartType, indicator]);

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ marginBottom: 24 }}>
        <LineChartOutlined style={{ marginRight: 8 }} />
        数据图表
      </h1>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <span>时间范围：</span>
          <Radio.Group value={timeRange} onChange={e => setTimeRange(e.target.value)}>
            <Radio.Button value="7d">7天</Radio.Button>
            <Radio.Button value="30d">30天</Radio.Button>
            <Radio.Button value="90d">90天</Radio.Button>
            <Radio.Button value="1y">1年</Radio.Button>
          </Radio.Group>

          <span style={{ marginLeft: 16 }}>图表类型：</span>
          <Radio.Group value={chartType} onChange={e => setChartType(e.target.value)}>
            <Radio.Button value="single">单指标</Radio.Button>
            <Radio.Button value="dual">双指标对比</Radio.Button>
          </Radio.Group>

          {chartType === 'single' && (
            <>
              <span style={{ marginLeft: 16 }}>指标：</span>
              <Select
                value={indicator}
                onChange={setIndicator}
                style={{ width: 100 }}
                options={[
                  { label: '血压', value: 'bp' },
                  { label: '血糖', value: 'bs' }
                ]}
              />
            </>
          )}
        </Space>
      </Card>

      <Row gutter={16}>
        <Col span={24}>
          <Card title={`${timeRange === '7d' ? '7天' : timeRange === '30d' ? '30天' : timeRange === '90d' ? '90天' : '1年'}趋势`}>
            {chartType === 'single' ? (
              <LineChart data={chartData} type={indicator} />
            ) : (
              <DualAxisChart data={chartData} />
            )}
          </Card>
        </Col>
      </Row>

      <Alert
        message="图表说明"
        description="血压正常范围：收缩压90-140 mmHg，舒张压60-90 mmHg。血糖正常范围：空腹3.9-6.1 mmol/L，餐后2h < 7.8 mmol/L。"
        type="info"
        showIcon
        style={{ marginTop: 16 }}
      />
    </div>
  );
};

export default Charts;
